import {
  FunctionComponent,
  MouseEventHandler,
  PointerEventHandler,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { useMountItemContext } from "./Mount";
import { useEvent } from "@app/hooks/useEvent";
import { IconButton } from "./Button";
import { CloseIcon } from "./icons";

export type ModalProps = PropsWithChildren<{
  title?: ReactNode;
  padding?: boolean;
}>;

/**
 * A modal dialog, on the native `<dialog>` element.
 *
 * On a phone it is a sheet against the bottom of the screen — that is where a thumb
 * is, and a box floating in the middle of a tall viewport leaves the keyboard to cover
 * whichever half it lands on. From the tablet breakpoint up it is a centred card.
 */
export const Modal: FunctionComponent<ModalProps> = ({
  title,
  children,
  padding = true,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const mount = useMountItemContext();
  /*
   * Where the press started. A click on the backdrop targets the <dialog> itself — but
   * so does one that began on text inside and finished outside, which is what selecting
   * a name and dragging past the edge looks like. Requiring both ends to be on the
   * backdrop is the difference between dismissing on purpose and dismissing by accident.
   */
  const startedOnBackdrop = useRef(false);

  const handlePointerDown = useEvent<PointerEventHandler<HTMLDialogElement>>(
    (e) => {
      startedOnBackdrop.current = e.target === e.currentTarget;
    }
  );

  const handleClick = useEvent<MouseEventHandler<HTMLDialogElement>>((e) => {
    if (startedOnBackdrop.current && e.target === e.currentTarget) {
      mount.close();
    }
    startedOnBackdrop.current = false;
  });

  const handleClose = useEvent(() => {
    mount.close();
  });

  useEffect(() => {
    // Guarded because jsdom has no `showModal`: a test rendering this should see the
    // contents rather than a crash.
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
  }, []);

  return (
    <dialog
      ref={dialogRef}
      open={false}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      /*
       * Esc fires `cancel` before `close`. Both are routed through the mount so there
       * is one way out: without this the element closed itself while its entry stayed
       * on the mount stack, and the modal could never be opened again.
       */
      onCancel={(e) => {
        e.preventDefault();
        mount.close();
      }}
      onClose={handleClose}
      /*
       * `m-auto` is load-bearing on desktop. A modal <dialog> is centred by the user
       * agent with `inset: 0; margin: auto`, and Tailwind's preflight resets margin to
       * 0 on every element — which leaves only the inset and drops the dialog into the
       * top-left corner.
       */
      /*
       * `max-w-none` is load-bearing on a phone. The user agent puts
       * `max-width: calc(100% - 6px - 2em)` on a modal <dialog>, so `w-full` came out
       * 352px in a 390px viewport — a card floating 19px from each edge rather than a
       * sheet against them. The `md:` width below is narrower than that cap anyway, so
       * removing it changes nothing on a desktop.
       */
      className="mt-auto mb-0 max-h-[85dvh] w-full max-w-none overflow-y-auto
        overscroll-contain rounded-t-2xl border border-line bg-surface-raised p-0
        text-ink shadow-xl backdrop:bg-black/40
        md:m-auto md:w-[min(28rem,calc(100vw-2rem))] md:rounded-xl"
    >
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
          {title}
        </h2>
        <IconButton label="Close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={padding ? "p-4" : undefined}>{children}</div>
    </dialog>
  );
};
